<template>
  <div class="h-full">
    <div class="h-full flex flex-col">
      <div class="w-full pr-3 flex-grow overflow-auto sm:pr-10 pl-2 pb-2">
        <template v-if="!activeProvider">
          <div v-if="!fetching && tenantAuthProviders.length" class="font-500 mb-1 text-13">Active</div>
          <div v-if="!fetching && tenantAuthProviders.length" class="w-full mb-2 sm:py-3 grid gap-4 grid-cols-collection-mobile sm:grid-cols-collection">
            <ListCard class="h-20 py-2 bg-white rounded-20 cursor-pointer" v-for="authProvider in tenantAuthProviders" :key="authProvider.name" @click="onProviderClick(authProvider)">
              <div class="flex w-full h-full items-center">
                <div class="w-11 h-11">
                  <img alt="" class="h-11" :src="authProvider.icon" />
                </div>
                <div>
                  <div class="ml-2 font-500">{{ authProvider.name }}</div>
                </div>
              </div>
            </ListCard>
          </div>
          <div v-if="availableProviders.length" class="font-500 mb-1 text-13">Available</div>
          <div v-if="availableProviders.length" class="w-full sm:py-3 grid gap-4 grid-cols-collection-mobile sm:grid-cols-collection">
            <ListCard class="h-20 py-2 bg-white rounded-20 cursor-pointer" v-for="authProvider in availableProviders" :key="authProvider.name" @click="onProviderClick(authProvider)">
              <div class="flex w-full h-full items-center">
                <div class="w-11 h-11">
                  <img alt="" class="h-11" :src="authProvider.icon" />
                </div>
                <div>
                  <div class="ml-2 font-500">{{ authProvider.name }}</div>
                </div>
              </div>
            </ListCard>
          </div>
        </template>

        <template v-else>
          <FormLabel class="mt-2" label="Authentication Provider"></FormLabel>
          <div class="text-semi-dark-text text-13 flex items-center">
            <img alt="" class="h-7 mr-2" :src="activeProviderIcon" />
            <span class="font-500">{{ providerName }}</span>
          </div>
          <div v-if="activeProvider === 'FIREBASE'" class="w-full max-w-800">
            <div class="mt-2">
              <FormInput v-model="providerConfig.firebase_project_id" label="Project Id"></FormInput>
            </div>
            <div class="mt-2">
              <FormInput v-model="providerConfig.firebase_api_key" label="Web API Key"></FormInput>
            </div>
            <div class="mt-2">
              <FormInput v-model="providerConfig.firebase_client_email" label="Client Emаil"></FormInput>
            </div>
            <div class="mt-2 mb-5">
              <FormInput v-model="providerConfig.firebase_private_key" label="Private Key"></FormInput>
            </div>
            <div class="font-500 mt-3 mb-3 text-13">Sign-in providers</div>
            <CollapseCard :value="false" :scroll="false" class="shadow-card mb-4">
              <template #header>
                <div class="flex w-full justify-between">
                  <div class="flex items-center"><Icon name="email" class="w-4 h-4 text-gray-600 mr-2" /> <span class="text-13">Emаil/Password</span></div>
                  <div class="pr-1">
                    <Badge :success="providerConfig.email_signin" :text="providerConfig.email_signin ? 'Enabled' : 'Disabled'"></Badge>
                  </div>
                </div>
              </template>
              <template #content>
                <div class="px-4 py-2 flex border-t border-stroke border-solid border-opacity-20">
                  <div>Allow users to sign in using their email address and password.</div>
                  <div class="flex items-center ml-auto"><Toggle dense v-model="providerConfig.email_signin" @update:modelValue="onEmailSigninChange" /></div>
                </div>
                <div class="px-4 py-2 flex mb-1">
                  <div>Email link (passwordless sign-in)</div>
                  <div class="flex items-center ml-auto"><Toggle dense v-model="providerConfig.email_link_signin" /></div>
                </div>
              </template>
            </CollapseCard>
            <CollapseCard :value="false" :scroll="false" class="shadow-card mb-4">
              <template #header>
                <div class="flex w-full justify-between">
                  <div class="flex items-center"><Icon name="google_logo_2" class="w-4 h-4 text-gray-600 mr-2" /> <span class="text-13">Google</span></div>
                  <div class="pr-1">
                    <Badge :success="providerConfig.google_signin" :text="providerConfig.google_signin ? 'Enabled' : 'Disabled'"></Badge>
                  </div>
                </div>
              </template>
              <template #content>
                <div class="px-4 py-2 flex border-t border-stroke border-solid border-opacity-20">
                  <div>Google sign-in</div>
                  <div class="flex items-center ml-auto"><Toggle dense v-model="providerConfig.google_signin" /></div>
                </div>
                <div class="px-4 py-2 flex">
                  <FormInput v-model="providerConfig.google_client_id" label="Web client ID" class="w-full" />
                </div>
                <div class="px-4 py-2 flex mb-3">
                  <FormInput v-model="providerConfig.google_client_secret" label="Web client secret" class="w-full" />
                </div>
              </template>
            </CollapseCard>
            <div class="flex w-full justify-end">
              <PillButton :disabled="saving" :loading="saving" primary text="Save" @click="onSave"></PillButton>
            </div>
          </div>
          <div v-else-if="activeProvider === 'API_KEY'" class="w-full py-1 max-w-800">
            <div class="mt-2 mb-3">
              <FormInput v-model="providerConfig.api_key" label="API Key"></FormInput>
            </div>
            <div class="flex w-full justify-end">
              <PillButton :disabled="saving" :loading="saving" primary text="Save" @click="onSave"></PillButton>
            </div>
          </div>
        </template>
      </div>
    </div>
    <Modal v-if="fetching">
      <div class="flex flex-col font-700 items-center justify-center px-20 my-10">
        <div>{{ $t('loading_data') }}</div>
        <Icon name="loading_dots" class="w-14 mt-3 text-primary" />
      </div>
    </Modal>
  </div>
</template>
<script>
import { apiPutProjectAuthentication } from '@/helpers/api';
import { mapActions, mapGetters } from 'vuex';
// import debounce from '@/helpers/debounce';

const PROVIDER_CONFIG = {
  FIREBASE: {
    firebase_project_id: '',
    firebase_api_key: '',
    firebase_client_email: '',
    firebase_private_key: '',
    email_signin: false,
    email_link_signin: false,
    google_signin: false,
    google_client_id: '',
    google_client_secret: '',
  },
  API_KEY: {
    api_key: '',
  },
};

function throttle(fn, delay) {
  let lastTime = 0;
  // eslint-disable-next-line
  return function (...args) {
    const now = Date.now();
    if (now - lastTime >= delay) {
      fn.apply(this, args);
      lastTime = now;
    }
  };
}

export default {
  name: 'AuthenticationConfig',
  data() {
    return {
      fetching: false,
      saving: false,
      activeProvider: null,
      providerConfig: {},
      emailPasswordEnabled: false,
      authProviders: [
        {
          name: 'Firebase',
          value: 'FIREBASE',
          icon: '/img/dataadapters/firebase_authentication.png',
        },
        {
          name: 'API Key',
          value: 'API_KEY',
          icon: '/img/dataadapters/apikey.png',
        },
      ],
    };
  },
  computed: {
    ...mapGetters(['getProjectSecrets']),
    projectId() {
      return this.$route.params.projectId;
    },
    secrets() {
      return Object.keys(this.getProjectSecrets()).map((i) => ({ label: i, value: i }));
    },
    providerName() {
      return this.authProviders.find((i) => i.value === this.activeProvider)?.name || '';
    },
    activeProviderIcon() {
      return this.authProviders.find((i) => i.value === this.activeProvider)?.icon || '';
    },
    queryParams() {
      return this.$route.query;
    },
    availableProviders() {
      return this.authProviders.filter((i) => !Object.keys(this.tenantProviderConfig).includes(i.value));
    },
    tenantProviderConfig() {
      return this.$store.getters.getProjectAuthProviders();
    },
    tenantAuthProviders() {
      return Object.keys(this.tenantProviderConfig)
        .map((i) => {
          return this.authProviders.find((j) => j.value === i);
        })
        .filter((i) => i);
    },
  },
  methods: {
    ...mapActions(['showToastMessage', 'fetchProjectAuthProviders']),
    onEmailSigninChange(value) {
      if (!value) {
        this.providerConfig.email_link_signin = false;
      }
    },
    onProviderClick(provider) {
      this.activeProvider = provider.value;
      this.$router.push({ query: { provider: provider.value } });
    },
    async onSave() {
      this.saving = true;
      try {
        const response = await apiPutProjectAuthentication({
          project_id: this.projectId,
          provider: this.activeProvider,
          config: this.providerConfig,
        });
        if (response.status === 200) {
          await this.fetchData();
          this.showToastMessage({ message: this.$t('projects.authentication.updated_successfully'), type: 'success' });
        } else {
          this.showToastMessage({ title: response?.data?.message || this.$t('projects.authentication.failed_to_update'), type: 'error' });
        }
      } catch (e) {
        this.showToastMessage({ title: this.$t('projects.authentication.failed_to_update'), type: 'error' });
      }
      this.saving = false;
    },
    // eslint-disable-next-line
    fetchData: throttle(async function () {
      this.fetching = true;
      await this.fetchProjectAuthProviders({ projectId: this.projectId });

      if (this.queryParams.provider) {
        this.activeProvider = this.queryParams.provider;
      } else {
        this.activeProvider = null;
      }

      if (this.activeProvider in this.tenantProviderConfig) {
        this.providerConfig = structuredClone(this.tenantProviderConfig[this.activeProvider].config);
      } else {
        this.providerConfig = { ...PROVIDER_CONFIG[this.activeProvider] };
      }
      this.fetching = false;
    }, 500),
  },
  watch: {
    projectId: {
      handler() {
        this.fetchData();
      },
      immediate: true,
    },
    'queryParams.provider': {
      async handler(value) {
        if (value) {
          this.fetchData();
        } else {
          this.activeProvider = null;
          this.providerConfig = {};
        }
      },
      immediate: true,
    },
  },
};
</script>
